import React, { useState } from "react";
import styled, { css } from "styled-components";
import { ProgressSpinner } from 'primereact/progressspinner';

export interface ListProps {
  items: Array<any>;
  allLines?: Array<any>;
  open?: number;
  itemDetail?: any;
  icon?: any;
  translate: any;
  getDetails?: any;
  onClickItem?: (index: number) => void;
}

const Container = styled.div`
  :nth-child(odd) {
    background-color: #ffffff;
  }

  :nth-child(even) {
    background-color: #f3f3f3;
  }
`;

const Item = styled.div`
  display: flex;
  justify-content: space-between;
  cursor: pointer;
  padding: 0.9rem;
  height: 4rem;
  vertical-align: middle;
  border: solid 1px #e8e8e8;
  color: #484848;
`;

const ItemDetail = styled.div<{ error: string, loadingDetails: boolean }>`
  padding: 0.5rem;
  border: solid 1px #e8e8e8;
  color: #484848;

  ${(props) => (props.error || props.loadingDetails) && css`
    text-align: center;
  `}
`;

const Alert = styled.img`
height:1.6rem;
width:1.6rem;
`;

const Warning = styled.img`
height:1.4rem;
width:1.5rem;
margin-right:1rem;
`;


const DescriptionDiv = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow: hidden;
`;

const Ellipsis = styled.div`
  white-space: nowrap;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DateDiv = styled.div`
  font-size: 14px;
  color: #BCBCBC;
  display: inline-flex;

`;

const renderDetails = (item: any, itemDetails: any, translate: any, error: string) => {
  if (error) {
    return <p style={{ color: "red" }}>{error}</p>;
  } else {
    const itemToShow = itemDetails || item;
    return (
      <>
        <div>{itemToShow.title || itemToShow.tit}</div>
        <div dangerouslySetInnerHTML={{
          __html: itemToShow.txt || itemToShow.description || itemToShow.text,
        }} />
        {itemToShow.alertLines && itemToShow.alertLines.length > 0 && (
          <p>
            {`${translate("affectedLines")}: `}
            {itemToShow.alertLines.map((x: string, idx: number) => idx + 1 === itemToShow.alertLines.length ? x : `${x}, `)}
          </p>
        )}
        {itemToShow.afl && itemToShow.afl.length > 0 && (
          <p>
            {`${translate("affectedLines")}: `}
            {itemToShow.afl.map((x: string, idx: number) => idx + 1 === itemToShow.afl.length ? x : `${x}, `)}
          </p>
        )}
        {itemToShow.alertStops && itemToShow.alertStops.length > 0 && (
          <p>
            {`${translate("affectedStops")}: `}
            {itemToShow.alertStops.map((x: string, idx: number) => idx + 1 === itemToShow.alertStops.length ? x : `${x}, `)}
          </p>
        )}
        {itemToShow.attachedFileNotice && itemToShow.attachedFileNotice.length > 0 && (
          <a target="_blank"
            aria-label={`descargar ${itemToShow.attachedFileNotice[0].title}`}
            href={`https://preliferay.consorcidetransports.com${itemToShow.attachedFileNotice[0].downloadURL}`}>
            {itemToShow.attachedFileNotice[0].title}
          </a>
        )}
        {itemToShow.url && (
          <a target="_blank"
            href={itemToShow.url}>
            {itemToShow.url}
          </a>
        )}
        {itemToShow.picture && (
          <div style={{ textAlignLast: "center" }}>
            <img src={`data:image/png;base64, ${itemToShow.picture}`} alt="alert-img" height={200} />
          </div>
        )}
      </>
    )
  }
}

const editAfectedLines = (notice: any, allLines: Array<any>) => {
  const afl = notice.afl.map((line: number) => {
    let newLine: number | undefined;
    allLines.forEach((completeLine: any) => {
      if (completeLine.id === line) newLine = completeLine.cod;
    });

    return newLine;
  }).filter((line: string | number) => line);

  const thereAreAfl = afl && afl.length ? afl : undefined;

  return { ...notice, afl: thereAreAfl };
};

const onClick = (
  open: number,
  index: number,
  items: Array<any>,
  translate: any,
  getDetails: any,
  allLines: Array<any>,
  onClickItem: (index: number) => void,
  setLoadingDetails: (loadingDetails: boolean) => void,
  setItemDetails: (itemDetails: any) => void,
  setError: (error: any) => void,
) => {
  if (onClickItem) {
    setItemDetails(undefined);
    const itemToSet = open === index ? -1 : index;
    onClickItem(itemToSet);

    if (itemToSet > -1) {
      const itemToSearch = items[itemToSet];

      if (itemToSearch && !itemToSearch.idAlert) {
        setLoadingDetails(true);
        setError(undefined);
        getDetails.getNotification(itemToSearch.id).then((notification: any) => {
          if (notification.type === "NOTICES") {
            if (notification && notification.data) getDetails.getAvisoSICTMWS(notification.data.liferayId).then((notice: any) => {
              setItemDetails(editAfectedLines(notice, allLines));
              setLoadingDetails(false);
            }).catch(() => { setError(translate("noticeError")); setLoadingDetails(false); })
          }
          if (notification.type === "TIMETABLE") {
            setItemDetails(notification);
            setLoadingDetails(false);
          }
        }).catch(() => { setError(translate("notificationError")); setLoadingDetails(false); });
      }
    }
  }
}

const List: React.FC<ListProps> = ({
  items,
  allLines,
  translate,
  open,
  icon,
  getDetails,
  onClickItem,
}) => {
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [itemDetails, setItemDetails] = useState();
  const [error, setError] = useState();

  return (
    <>
      {items &&
        items.length > 0 &&
        items.map((item: any, index: number) => {
          return (
            <Container key={index}>
              <Item
                data-testid="list-item"
                onClick={() =>
                  onClick(open, index, items, translate, getDetails, allLines, onClickItem, setLoadingDetails, setItemDetails, setError)}
              >
                {onClickItem ? <Warning src={icon} alt="icon" /> : <Alert src={icon} alt="icon" />}
                <DescriptionDiv>
                  <DateDiv>{item.creationDate || item.creation_date}</DateDiv>
                  <Ellipsis>{item.tit || item.title || item.text}</Ellipsis>
                </DescriptionDiv>
                {onClickItem && (
                  <button style={{ color: "#dd052b", boxShadow: "none" }} className="p-link" >
                    <i style={{ fontSize: '1.3em' }} className={`pi pi-chevron-${open === index ? "up" : "down"}`} />
                  </button>
                )}
              </Item>
              {
                open === index ? (
                  <ItemDetail error={error} loadingDetails={loadingDetails}>
                    {loadingDetails ? (
                      <ProgressSpinner style={{ height: "50px" }} animationDuration="1s" />
                    ) : (
                      renderDetails(item, itemDetails, translate, error)
                    )}
                  </ItemDetail>
                ) : null
              }
            </Container>
          );
        })}
    </>
  );
}

export default List;
